import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import { useAlert } from "contexts/alert";
import { useAuth } from "contexts/auth";
import { cpfMask } from "utils/cpf";
import api from "services/api";

import { Box, Button, Checkbox, FormControl, InputAdornment, InputLabel, MenuItem, Modal, Select, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import { Close } from "@mui/icons-material";
import { useHistory, useParams } from "react-router-dom";
import { EditarDadosFaltantes } from "../EditarDadosFaltantes";
import { ErroAoCarregar, intervaloQueParaSemAcesso, tentarDeNovoAoCarregar } from "../ErroAoCarregar";

function formatDate(dateToFormat: string) {
  try {
    if (!dayjs(dateToFormat).isValid()) throw new Error();
    
    return dayjs(dateToFormat).format(`DD/MM/YYYY - HH:mm:ss`);
  } catch (error) {
    return `--/--/---- --:--:--`
  }
}

export interface IMissingDataResponse {
  PERSON_NAME: string;
  PERSON_TYPE: "employee" | "dependent";
  PERSON_TAX_PAYER_ID: string;
  /** If current person is of type 'dependent', this specific field will always be 'null'; However, if
   * current person is 'employee', then this field could either be a 'string', meaning it contains the
   * data, or null, meaning its empty and must be filled out on the form sent via e-mail.
   */
  EMPLOYEE_MOTHER_NAME: string | null;
  ORGANIZATION_ID: string | null;
  PERSON_BIRTH_DATE: string | null;
}

interface IFilters {
  personName: string,
  personType: 'all' | 'employee' | 'dependent',
}

/** D15: veredito real do envio, devolvido por src/modules/missing-data/use-cases/send-missing-data.use-case.ts. */
type MissingDataSendOutcome = 'SENT' | 'NO_EMAIL' | 'SEND_FAILED' | 'NOT_FOUND' | 'NOT_SAVED';

interface ISendMissingDataBatchResponse {
  sent: number;
  noEmail: number;
  failed: number;
  notFound: number;
  /** D32: titular encontrado, mas o cadastro não gravou; o e-mail não saiu. */
  notSaved: number;
  results: { taxPayerId: string; outcome: MissingDataSendOutcome }[];
}

/**
 * Buyer deve:
 * - Buscar endpoint APENAS da diferença entre pessoas que estão em MASSA_ELEGIVEL e CAPTURE_EMPLOYEES
 * - Para essas pessoas: poder selecionar quais quer enviar email através de um checkpoint
 * - Botão para enviar emails (conectar a um POST)
 *
 * Atenção:
 * - Diferente da view do Seller, não incluir coluna de STATUS (não terá esse valor)
 */
export function MissingDataBuyer({ buyerOrganizationId }: { buyerOrganizationId: string }) {
  const { workspaceId, templateId } = useParams() as { workspaceId: string; templateId: string; };
  const { openAlert } = useAlert();
  const history = useHistory();

  const [currentFilters, setCurrentFilters] = useState<IFilters>({
    personName: '',
    personType: 'all',
  });

  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [sentRows, setSentRows] = useState<string[]>([]);

  const [isModalEmailsOpen, setIsModalEmailsOpen] = useState(false);
  const [isSendingEmails, setIsSendingEmails] = useState(false);

  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);

  const [isEdit, setIsEdit] = useState(false);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 5));
    setPage(0);
  };

  const { data, isError, error } = useQuery({
    queryKey: ["missing-data", workspaceId],
    queryFn: () => api.get<{ data: IMissingDataResponse[] }>(`/missing-data/get-missing-data/${buyerOrganizationId}/${workspaceId}`),
    select: (data) => data?.data?.data,
    retry: tentarDeNovoAoCarregar,
    refetchInterval: intervaloQueParaSemAcesso(5000)
  });

  const handleFilters = (filters: IFilters) => {
    setCurrentFilters({ ...filters });
    setPage(0);
  };

  // why: era `if (!data) return <div>Loading</div>`. Com o 404 que as rotas de
  // /missing-data devolvem a quem não é parte do contrato (v0.0.167), `data`
  // nunca chega e a tela ficava carregando para sempre.
  if (!data) {
    if (isError) return <ErroAoCarregar error={error} />;
    return <div>Carregando...</div>;
  }

  const isSelected = (id: string) => selectedRows.includes(id);

  const handleSelectRow = (id: string) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id]
    );
  };

  // Mesma lista, filtrada uma única vez, usada tanto pelas linhas exibidas quanto pelo total da
  // paginação (D37): antes o rodapé contava um universo diferente do que a tabela mostrava, incluindo
  // pessoas já enviadas e ignorando a busca por nome/CPF.
  const filteredData = data
    .filter(person => !sentRows.includes(person.PERSON_TAX_PAYER_ID))
    .filter(person => (person?.PERSON_NAME?.toLowerCase().includes(currentFilters.personName.toLowerCase()) || person?.PERSON_TAX_PAYER_ID?.toLowerCase().includes(currentFilters.personName.toLowerCase())) && (currentFilters.personType === 'all' || currentFilters.personType === person.PERSON_TYPE));

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = filteredData.map((row) => row.PERSON_TAX_PAYER_ID);
      return setSelectedRows(allIds);
    }
    
    setSelectedRows([]);
  };

  const handleSubmit = async () => {
    try {
      setIsSendingEmails(true);
      const people = data.filter(person => person.PERSON_TYPE === 'employee' && selectedRows.includes(person.PERSON_TAX_PAYER_ID));

      const response = await api.post<ISendMissingDataBatchResponse>(`/missing-data/send-missing-data/batch`, {
        context: {
          organizationId: buyerOrganizationId,
          workspaceId: workspaceId,
        },
        people: people.map(person => ({
          TAX_PAYER_ID: person.PERSON_TAX_PAYER_ID,
          TYPE_PERSON: person.PERSON_TYPE,
        }))
      });

      // D15 (Onde a Plataforma Mente, 18/08/2026): só sai da lista quem teve o envio
      // confirmado. "Sem e-mail", "falhou" e "não encontrado" continuam na lista,
      // porque nada foi resolvido para essas pessoas.
      // hazard: se este frontend subir antes do backend #1766, a resposta vem no
      // shape antigo (sem outcome) e a tela pintaria "0 enviado(s)" em verde.
      if (!Array.isArray(response.data?.results) || (response.data.results.length > 0 && response.data.results[0]?.outcome === undefined)) {
        openAlert('O envio foi processado, mas a resposta veio num formato que esta tela não reconhece. Atualize a página para ver o estado real.', 'warning');
        return;
      }

      const { sent, noEmail, failed, notFound, notSaved, results } = response.data;
      const sentTaxPayerIds = (results ?? []).filter(result => result.outcome === 'SENT').map(result => result.taxPayerId);

      setSentRows((prev) => ([...prev, ...sentTaxPayerIds]));
      setSelectedRows((prev) => prev.filter(id => !sentTaxPayerIds.includes(id)));

      const parts = [`${sent ?? 0} enviado(s)`];
      if (noEmail) parts.push(`${noEmail} sem e-mail cadastrado`);
      if (failed) parts.push(`${failed} falharam no envio`);
      if (notFound) parts.push(`${notFound} não encontrados na base`);
      if (notSaved) parts.push(`${notSaved} não gravados no cadastro`);

      // why: qualquer pessoa que ficou sem e-mail (sem endereço, falha, não gravada,
      // não encontrada) é um resultado a tratar, não um sucesso. Antes, "0 enviados,
      // 11 sem e-mail" saía verde.
      const unresolved = (noEmail ?? 0) + (failed ?? 0) + (notFound ?? 0) + (notSaved ?? 0);
      const severity = unresolved === 0 ? 'success' : (sent ? 'warning' : 'error');
      openAlert(parts.join(', '), severity);
    } catch (error) {
      openAlert('Houve algum error no envio de e-mails', 'error');
    } finally {
      setIsSendingEmails(false);
      setIsModalEmailsOpen(false);
    }
  }

  const formatMissingData = (person: IMissingDataResponse) => {
    const isEmployee = person.PERSON_TYPE === 'employee' ? true : false;

    const missingData = Object.entries(person).map(([key, value]) => {
      if (key === "PERSON_TAX_PAYER_ID" && !value) return "CPF";
      if (key === "PERSON_BIRTH_DATE" && !value) return "Data nascimento";
      if (key === "PERSON_GENDER_ID" && !value) return "Gênero";
      if (key === "PERSON_NAME" && !value) return "Nome";
      if (isEmployee && key === "EMPLOYEE_MOTHER_NAME" && !value) return "Nome da mãe";
      if (isEmployee && key === "EMPLOYEE_MARITAL_STATUS_ID" && !value) return "Estado civil";
      if (isEmployee && key === "EMPLOYEE_ZIP_CODE" && !value) return "CEP";
      if (isEmployee && key === "EMPLOYEE_ADDRESS_TYPE" && !value) return "Tipo do endereço";
      if (isEmployee && key === "EMPLOYEE_ADDRESS" && !value) return "Endereço";
      if (isEmployee && key === "EMPLOYEE_ADDRESS_NUMBER" && !value) return "Número do endereço";
      if (isEmployee && key === "EMPLOYEE_COMPLEMENT" && !value) return "Complemento";
      if (isEmployee && key === "EMPLOYEE_NEIGHBORHOOD" && !value) return "Bairro";
      if (isEmployee && key === "EMPLOYEE_STATE_ID" && !value) return "Estado";
      if (isEmployee && key === "EMPLOYEE_CITY_ID" && !value) return "Cidade";
      if (isEmployee && key === "EMPLOYEE_HIRING_DATE" && !value) return "Data admissão";
      if (isEmployee && key === "EMPLOYEE_REGISTRATION_NUMBER" && !value) return "RG";
      if (isEmployee && key === "EMPLOYEE_WEDDING_DATE" && !value) return "Data de casamento";
    })
    .filter(person => !!person)
    .join(", ");

    return missingData || "-";
  };

  const panels = !data ? {} : {
    "Total": data.length - sentRows.length,
    "Titulares": data.filter(item => !sentRows.includes(item.PERSON_TAX_PAYER_ID)).filter(item => item.PERSON_TYPE === 'employee').length,
    "Dependentes": data.filter(item => !sentRows.includes(item.PERSON_TAX_PAYER_ID)).filter(item => item.PERSON_TYPE === 'dependent').length,
  };

  function handleClickCampanhas() {
    history.push(`/v2/workspaces/${workspaceId}/templates/${templateId}/campanhas/${workspaceId}`)
  }

  return (
    <>
      {!isEdit && (
        <>
          <Modal
            open={isModalEmailsOpen}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box
              sx={{
                position: "absolute" as "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                height: "auto",
                width: "100%",
                maxWidth: 620,
                bgcolor: "background.paper",
                boxShadow: 24,
                borderRadius: "1rem",
                border: "none",
                outline: "none",
                padding: "24px",
              }}
            >
              <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
                <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Gostaria de enviar email para as pessoas selecionadas?</h6>
                <Close
                  style={{"cursor":"pointer"}}
                  onClick={() => setIsModalEmailsOpen(false)}
                />
              </div>
              {/* <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '34px', marginTop: '24px' }}>
                <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Dados atualizados em</p>
                <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: '600', color: '#6F6F78' }}>{formatDate("24/06/2002")}</span>
              </div> */}
              <div style={{ marginBottom: "100px"}} />
      
              <div style={{"display":"flex","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"1rem","justifyContent":"flex-end","alignItems":"center","width":"100%"}}>
                <div style={{"display":"flex","gap":"0.25rem"}}>
                  <Button
                    variant='outlined'
                    color='error'
                    style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                    disabled={isSendingEmails}
                    onClick={() => setIsModalEmailsOpen(false)}
                  >
                    Não, vou verificar novamente
                  </Button>
      
                  <Button
                    variant='contained'
                    style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                    disabled={isSendingEmails}
                    onClick={handleSubmit}
                  >
                    Sim, enviar
                  </Button>
                </div>
              </div>
            </Box>
          </Modal>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h1 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600,marginBottom: '24px'}}>Gestão de dados faltantes</h1>

            {/** Botoes a direita (grid, table, etc) */}
            <div style={{"display":"flex","gap":"0.25rem", alignItems: "center"}}>
              <Button
                variant="outlined"
                style={{
                  paddingTop: "0.25rem",
                  paddingBottom: "0.25rem",
                  paddingLeft: "1rem",
                  paddingRight: "1rem",
                  borderRadius: "8px",
                  height: "30px",
                  fontSize: "0.875rem",
                  lineHeight: "1.25rem",
                  fontWeight: 600,
                }}
                // onClick={() => setIsModalStatusOpen(true)}
              >
                Atividades relacionadas
              </Button>
            </div>
          </div>

          {/** Interface de Data */}
          <div style={{ display: 'flex', flexDirection: 'row', gap: '50px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' }}>
              <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>Dados atualizados em</p>
              {/* Sem uma coluna real de "atualizado em" na resposta desta tela ainda, formatDate("")
                  cai no fallback existente (tracinhos) em vez de afirmar uma data que não aconteceu (D36). */}
              <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: '600', color: '#6F6F78' }}>{formatDate("")}</span>
            </div>
          </div>

          {/** Métricas (Total, Inclusoes, etc) */}
          <div style={{"display":"flex","gap":"1rem"}}>
            {Object.entries(panels).map(([title, value]) => (
              <div style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column', marginBottom: '30px' }}>
                <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78' }}>{title}</p>
                <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{value}</span>
              </div>
            ))}
          </div>

          {/** SELECTS E FILTROS */}
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <TextField
              fullWidth
              label='Nome beneficiário ou CPF'
              placeholder='Digite para pesquisar'
              variant='outlined'
              value={currentFilters.personName}
              onChange={(e) => handleFilters({ ...currentFilters, personName: e.target.value  })}
              InputProps={{ 
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon color='action' style={{ width: '24px', aspectRatio: '1' }}/>
                  </InputAdornment>
                )
              }}
            />
            <div style={{ width: '100%', display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center', marginBottom: '24px' }}>
              <Typography color='gray' variant='body2'>Filtrar por</Typography>
              <FormControl style={{ width: '165px' }}>
                <InputLabel id="personType">Parentesco</InputLabel>
                <Select
                  labelId="personType"
                  label="Parentesco"
                  value={currentFilters.personType}
                  defaultValue={'all'}
                  onChange={(e) => handleFilters({ ...currentFilters, personType: e.target.value === 'employee' ? 'employee' : e.target.value === 'dependent' ? 'dependent' : 'all' })}
                  style={{ height: '40px' }}
                >
                  <MenuItem value='all'>Todos</MenuItem>
                  <MenuItem value='employee'>Titular</MenuItem>
                  <MenuItem value='dependent'>Dependente</MenuItem>
                </Select>
              </FormControl>
              <Button onClick={() => handleFilters({ personName: '', personType: 'all' })}>Limpar filtro</Button>
            </div>
          </div>

          {/** TABELA */}
          <TableContainer sx={{ "& .MuiTableCell-root": { background: "#FFFFFF !important" } }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={selectedRows.length > 0 && selectedRows.length < data.length}
                      checked={data.length > 0 && selectedRows.length === data.length}
                      onChange={handleSelectAll}
                      inputProps={{ 'aria-label': 'Selecionar todas as linhas' }}
                    />
                  </TableCell>
                  <TableCell>Nome</TableCell>
                  <TableCell>CPF/CNPJ</TableCell>
                  <TableCell>Parentesco</TableCell>
                  <TableCell>Campos vazios</TableCell>
                  {/* <TableCell>Status formulário</TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(person => (
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected(person.PERSON_TAX_PAYER_ID)}
                        onChange={() => handleSelectRow(person.PERSON_TAX_PAYER_ID)}
                        inputProps={{ 'aria-label': `Selecionar linha ${person.PERSON_TAX_PAYER_ID}` }}
                      />
                    </TableCell>
                    <TableCell>{person.PERSON_NAME}</TableCell>
                    <TableCell>{cpfMask(person.PERSON_TAX_PAYER_ID)}</TableCell>
                    <TableCell>{person.PERSON_TYPE === 'employee' ? "Titular" : "Dependente"}</TableCell>
                    <TableCell>{formatMissingData(person)}</TableCell>
                    {/* <TableCell>-</TableCell> */}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />

          {/** BOTOES */}
          <div style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'end',"paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"24px", gap: "20px" }}>
            <Button
              variant='outlined'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={() => setIsEdit(true)}
            >
              Editar dados
            </Button>
            <Button
              variant='outlined'
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={handleClickCampanhas}
              // disabled
            >
              Campanhas
            </Button>
            <Button
              variant='contained'
              disabled={!selectedRows.length}
              style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
              onClick={() => setIsModalEmailsOpen(true)}
            >
              Enviar e-mails
            </Button>
          </div>
        </>
      )}
      {isEdit && (
        <EditarDadosFaltantes data={data} setIsEdit={setIsEdit}/>
      )}
    </>
  );
}
